import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { BookOpen, Sparkles, Scroll, Heart, Sun, Star } from 'lucide-react'
import { inspirationalQuotes } from '@/data/frameworks'

export default function Step1() {
  const [expandedPath, setExpandedPath] = useState<string | null>(null)
  const [quoteIndex, setQuoteIndex] = useState(0)
  const [showQuoteDetail, setShowQuoteDetail] = useState(false)

  const wisdomPaths = [
    {
      icon: BookOpen,
      title: 'Sacred Scriptures',
      description: 'Ancient texts preserving divine revelation',
      detail: 'From the Torah scrolls to the Quran, the Bhagavad Gita to the Pali Canon, sacred scriptures form the bedrock of spiritual traditions. These texts have been preserved, transmitted, and interpreted across millennia.',
      color: 'from-gold-400 to-gold-600'
    },
    {
      icon: Sparkles,
      title: 'Mystical Traditions',
      description: 'Esoteric teachings of spiritual masters',
      detail: 'Kabbalah, Sufism, Christian mysticism, Advaita Vedanta -- the mystical paths across traditions share a common thread: direct experience of the divine beyond intellectual understanding.',
      color: 'from-purple-400 to-purple-600'
    },
    {
      icon: Heart,
      title: 'Devotional Paths',
      description: 'Love and worship of the divine',
      detail: 'Bhakti yoga, Sufi poetry, Hasidic devotion, charismatic worship -- devotional traditions channel love and emotion as the primary vehicle for connecting with the sacred.',
      color: 'from-rose-400 to-rose-600'
    },
    {
      icon: Sun,
      title: 'Prophetic Wisdom',
      description: 'Messages delivered through messengers',
      detail: 'Moses, Muhammad, the Hebrew prophets, Zoroaster -- prophetic traditions claim divine communication through chosen individuals who transmit guidance to humanity.',
      color: 'from-amber-400 to-amber-600'
    },
    {
      icon: Scroll,
      title: 'Philosophical Inquiry',
      description: 'Rational exploration of ultimate truth',
      detail: 'From Aristotle and Plato through Maimonides, Aquinas, and Al-Farabi to modern philosophy of religion -- rational inquiry seeks truth through logic and evidence.',
      color: 'from-blue-400 to-blue-600'
    },
    {
      icon: Star,
      title: 'Oral Traditions',
      description: 'Wisdom passed through generations',
      detail: 'The Oral Torah, Aboriginal Dreamtime, Native American wisdom keepers, West African griots -- oral traditions preserve and transmit sacred knowledge through living memory.',
      color: 'from-emerald-400 to-emerald-600'
    }
  ]

  const currentQuote = inspirationalQuotes[quoteIndex % inspirationalQuotes.length]

  const nextQuote = () => {
    setShowQuoteDetail(false)
    setQuoteIndex(prev => (prev + 1) % inspirationalQuotes.length)
  }

  const prevQuote = () => {
    setShowQuoteDetail(false)
    setQuoteIndex(prev => (prev - 1 + inspirationalQuotes.length) % inspirationalQuotes.length)
  }

  return (
    <div className="space-y-12">
      {/* Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-3xl mx-auto"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 mb-6 shadow-divine">
          <BookOpen className="w-10 h-10 text-white" />
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-gold-gradient mb-6">
          Sacred Wisdom Across Traditions
        </h1>
        <p className="text-lg text-gold-700/80 dark:text-gold-300/80 leading-relaxed">
          Throughout human history, divine wisdom has been revealed through prophets, sages,
          and enlightened teachers. These sacred teachings offer guidance for living a meaningful
          life and understanding our place in the cosmos.
        </p>
      </motion.div>

      {/* Featured Quote - Interactive Carousel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="sacred-card p-8 max-w-2xl mx-auto divine-glow"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={quoteIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="text-center"
          >
            <span className="text-6xl text-gold-400 font-display">"</span>
            <blockquote className="font-serif text-2xl italic text-gold-800 dark:text-gold-200 -mt-4 mb-4">
              {currentQuote.text}
            </blockquote>
            <footer className="text-gold-600 dark:text-gold-400">
              <cite className="font-medium">-- {currentQuote.source}</cite>
              <span className="mx-2">&#8226;</span>
              <span className="text-sm">{currentQuote.tradition}</span>
            </footer>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-center gap-4 mt-4">
          <motion.button
            onClick={prevQuote}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="px-3 py-1 rounded-lg bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 text-sm transition-colors duration-300 hover:bg-gold-200 dark:hover:bg-gold-800/40"
          >
            Previous
          </motion.button>
          <span className="text-xs text-gold-500">{quoteIndex + 1} / {inspirationalQuotes.length}</span>
          <motion.button
            onClick={nextQuote}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="px-3 py-1 rounded-lg bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 text-sm transition-colors duration-300 hover:bg-gold-200 dark:hover:bg-gold-800/40"
          >
            Next
          </motion.button>
        </div>

        <motion.button
          onClick={() => setShowQuoteDetail(!showQuoteDetail)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full mt-3 px-3 py-2 text-xs font-medium rounded-lg bg-gold-50 dark:bg-gold-900/20 text-gold-600 dark:text-gold-400 border border-gold-200 dark:border-gold-800/30 transition-all duration-300"
        >
          {showQuoteDetail ? 'Hide Context' : 'Explore This Teaching'}
        </motion.button>

        <AnimatePresence>
          {showQuoteDetail && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-3 p-3 rounded-lg bg-gold-50/50 dark:bg-gold-900/10 border border-gold-200/50 dark:border-gold-800/20">
                <p className="text-xs text-gold-700/70 dark:text-gold-300/70">
                  This teaching from the <strong>{currentQuote.tradition}</strong> tradition reflects a core insight about the nature of divine wisdom and the human journey toward understanding.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Wisdom Paths Grid - Interactive */}
      <div>
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="font-display text-2xl font-semibold text-center text-gold-800 dark:text-gold-200 mb-8"
        >
          Pathways of Divine Knowledge
        </motion.h2>

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.1 }
            }
          }}
        >
          {wisdomPaths.map((path) => {
            const Icon = path.icon
            const isExpanded = expandedPath === path.title
            return (
              <motion.div
                key={path.title}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0 }
                }}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setExpandedPath(isExpanded ? null : path.title)}
                className="sacred-card p-6 group cursor-pointer transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${path.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold text-gold-800 dark:text-gold-200 mb-2">
                  {path.title}
                </h3>
                <p className="text-sm text-gold-600/70 dark:text-gold-400/70">
                  {path.description}
                </p>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 pt-4 border-t border-gold-200 dark:border-gold-800/30">
                        <p className="text-sm text-gold-700/70 dark:text-gold-300/70 leading-relaxed">
                          {path.detail}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-3 text-right">
                  <motion.span
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    className="inline-block text-gold-400 text-xs"
                  >
                    {'▼'}
                  </motion.span>
                </div>
              </motion.div>
            )
          })}
        </motion.div>
      </div>

      {/* Sacred insight */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="bg-gradient-to-r from-gold-100 via-gold-50 to-gold-100 dark:from-gold-900/20 dark:via-gold-800/10 dark:to-gold-900/20 rounded-2xl p-8 text-center"
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-center gap-4 mb-4 text-3xl">
            <span>✝</span>
            <span>☪</span>
            <span>✡</span>
            <span>🕉</span>
            <span>☸</span>
            <span>☬</span>
          </div>
          <p className="font-serif text-lg text-gold-800 dark:text-gold-200 italic">
            "All rivers flow to the same ocean. All spiritual paths lead seekers toward the same
            ultimate truth -- the divine source from which all wisdom springs."
          </p>
        </div>
      </motion.div>
    </div>
  )
}
